<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Licencias y permisos</h1>
    </div>

    <wz-table-container>
      <wz-table-toolbar>
        <template #left>
          <wz-filter-menu
            :options="filterOptions"
            :selected-option-filter="filters"
            @setFilters="setFilters"
          />
        </template>
        <template #right>
          <wz-button v-if="selectedIds.length > 0" variant="danger" @click="openBatchAnnul()">
            <i class="ri-close-circle-line mr-1"></i>{{ selectedIds.length > 1 ? `Anular ${selectedIds.length} seleccionadas` : 'Anular' }}
          </wz-button>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData()">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-tooltip text="Descargar Excel">
            <wz-button variant="outline" @click="downloadReport">
              <i class="ri-file-excel-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-search-bar
            placeholder="Buscar por nombre de colaborador"
            @search="onSearch"
            @input="search = $event"
          />
          <wz-button variant="primary" @click="$router.push({ name: 'licenses-create' })">Nueva licencia</wz-button>
        </template>
        <template #filters v-if="showFiltersList">
          <wz-badge
            v-for="(val, name) in filterList"
            :key="name"
            variant="primary"
            pill
            dismissible
            @dismiss="deleteFilter(name)"
          >
            {{ getFilterName(name, val) }}
          </wz-badge>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th style="width: 36px; text-align: center;">
              <input type="checkbox" :checked="allSelected" @change="toggleSelectAll" title="Seleccionar todos" />
            </th>
            <th>Colaborador</th>
            <th>Tipo</th>
            <th>Familiar</th>
            <th>Fecha inicio</th>
            <th>Fecha fin</th>
            <th style="text-align: right">Días</th>
            <th>Estado</th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="list.length === 0">
            <td colspan="9" class="text-center text-muted py-4">No se encontraron licencias</td>
          </tr>
          <tr v-for="item in list" :key="item.id">
            <td style="text-align: center">
              <input type="checkbox" :value="item.id" v-model="selectedIds" />
            </td>
            <td>{{ item.employeeFullName }}</td>
            <td>{{ item.leaveTypeName }}</td>
            <td>{{ item.beneficiaryName || '-' }}</td>
            <td v-local-date="item.startDate"></td>
            <td v-local-date="item.endDate"></td>
            <td style="text-align: right">{{ item.calendarDays }}</td>
            <td>
              <wz-badge :variant="statusColor(item.status)" pill>{{ statusLabel(item.status) }}</wz-badge>
            </td>
            <td style="text-align: center">
              <wz-dropdown
                :items="rowActions(item)"
                placement="left"
                @click="handleAction(item, $event)"
              />
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <wz-pagination
          v-if="list.length"
          :total="total"
          :current-page="page"
          :per-page="pageSize"
          @change="onPaginationChange"
        />
        <div class="table-no-results" v-else>
          <p class="m-0 px-3">No se encontraron resultados.</p>
        </div>
      </wz-table-footer>
    </wz-table-container>

    <input
      ref="docInput"
      type="file"
      accept=".pdf,.jpg,.jpeg,.png"
      style="display: none"
      @change="onDocSelected"
    />

    <wz-confirm-modal
      v-model="confirmCancel.show"
      title="Cancelar licencia"
      description="¿Está seguro que desea cancelar esta licencia?"
      accept-text="Cancelar licencia"
      accept-variant="danger"
      @confirm="doCancel"
    />

    <annul-by-mistake-modal
      v-model="showAnnul"
      :submitting="annulSubmitting"
      :entity-description="annulTarget ? `${annulTarget.employeeFullName} — ${annulTarget.leaveTypeName || 'licencia'} del ${annulTarget.startDate} al ${annulTarget.endDate}` : ''"
      @confirm="doAnnul"
    />

    <annul-by-mistake-modal
      v-model="showBatchAnnul"
      :submitting="batchSubmitting"
      :entity-descriptions="batchDescriptions"
      @confirm="doBatchAnnul"
    />
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { FilterMixin } from "@wizepe/wizeui"
import { PaginationMixin } from "@wizepe/wizeui"
import { Constants as LocalConstants } from '@/core/utils'
import { LeaveService, LeaveTypeService, CompanyService } from '@/core/services'
import AnnulByMistakeModal from '@/core/components/AnnulByMistakeModal.vue'

export default {
  name: 'LeaveList',
  components: { AnnulByMistakeModal },
  mixins: [PaginationMixin, FilterMixin],
  data() {
    return {
      list: [],
      search: '',
      pendingDoc: false, // drill-down desde el dashboard: solo licencias sin documentación requerida
      docTargetId: null,
      confirmCancel: { show: false, id: null },
      showAnnul: false,
      annulTarget: null,
      annulSubmitting: false,
      selectedIds: [],
      showBatchAnnul: false,
      batchSubmitting: false,
      filterOptions: [
        { label: 'Empresa',          key: 'companyId',   type: LocalConstants.TypeFilters.SINGLE, options: [] },
        { label: 'Tipo de licencia', key: 'leaveTypeId', type: LocalConstants.TypeFilters.SINGLE, options: [] },
        { label: 'Estado',           key: 'status',      type: LocalConstants.TypeFilters.SINGLE,
          options: Object.values(LocalConstants.LeaveStatus).map(s => ({ key: s.key, label: s.label })) },
        { label: 'Rango de fechas',  key: 'date',        type: LocalConstants.TypeFilters.DATE, isRange: true },
      ],
    }
  },
  computed: {
    allSelected() {
      return this.list.length > 0 && this.list.every(i => this.selectedIds.includes(i.id))
    },
    batchDescriptions() {
      return this.list
        .filter(i => this.selectedIds.includes(i.id))
        .map(i => `${i.employeeFullName} — ${i.leaveTypeName || 'licencia'} del ${i.startDate} al ${i.endDate}`)
    },
  },
  methods: {
    toggleSelectAll(e) {
      this.selectedIds = e.target.checked ? this.list.map(i => i.id) : []
    },
    openBatchAnnul() {
      if (this.selectedIds.length > 0) this.showBatchAnnul = true
    },
    async doBatchAnnul(reason) {
      try {
        this.batchSubmitting = true
        const resp = await LeaveService.annulBatch(this.selectedIds, reason)
        const payload = resp.payload || resp
        const ok = (payload.succeeded || []).length
        const failed = payload.failed || []
        if (failed.length === 0) {
          Alert.success(`${ok} licencia(s) anulada(s) correctamente`)
        } else {
          const detail = failed.map(f => `#${f.id}: ${f.error}`).join('\n')
          Alert.alert('Advertencia', `${ok} anulada(s), ${failed.length} fallaron.\n${detail}`)
        }
        this.showBatchAnnul = false
        this.selectedIds = []
        this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al anular las selecciones')
      } finally {
        this.batchSubmitting = false
      }
    },
    statusColor(status) {
      const f = Object.values(LocalConstants.LeaveStatus).find(s => s.key === status)
      return f ? f.color : 'dark'
    },
    statusLabel(status) {
      const f = Object.values(LocalConstants.LeaveStatus).find(s => s.key === status)
      return f ? f.label : status
    },
    rowActions(item) {
      const actions = [{ key: 'detail', label: 'Ver detalle' }]
      if (item.status === 'REGISTRADA' || item.status === 'EN_CURSO') {
        actions.push({ key: 'edit', label: 'Editar' })
      }
      if (this.canCancel(item)) {
        actions.push({ key: 'cancel', label: 'Cancelar', danger: true })
      }
      if (item.status !== 'CANCELADA') {
        actions.push({ key: 'document', label: 'Subir documento' })
        actions.push({ key: 'annul', label: 'Anular', danger: true })
      }
      return actions
    },
    canCancel(item) {
      if (item.status !== 'REGISTRADA') return false
      const today = new Date().toISOString().split('T')[0]
      return item.startDate > today
    },
    handleAction(item, action) {
      if (action.key === 'detail')   this.$router.push({ name: 'licenses-detail', params: { id: item.id } })
      if (action.key === 'edit')     this.$router.push({ name: 'licenses-edit', params: { id: item.id } })
      if (action.key === 'cancel')   this.openCancel(item)
      if (action.key === 'document') this.triggerDocUpload(item)
      if (action.key === 'annul')    this.openAnnul(item)
    },
    openAnnul(item) { this.annulTarget = item; this.showAnnul = true },
    async doAnnul(reason) {
      try {
        this.annulSubmitting = true
        await LeaveService.annulLeave(this.annulTarget.id, reason)
        Alert.success('Licencia anulada por error')
        this.showAnnul = false
        this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al anular la licencia')
      } finally {
        this.annulSubmitting = false
      }
    },
    buildFilterParams() {
      const params = {}
      if (this.filters.companyId)   params.companyId   = this.filters.companyId
      if (this.filters.leaveTypeId) params.leaveTypeId = this.filters.leaveTypeId
      if (this.filters.status)      params.status      = this.filters.status
      if (this.filters.date && this.filters.date.start) params.dateFrom = this.filters.date.start
      if (this.filters.date && this.filters.date.end)   params.dateTo   = this.filters.date.end
      if (this.search) params.search = this.search
      if (this.pendingDoc) params.pendingDocumentation = true
      return params
    },
    onSearch(val) {
      this.search = val
      this.page = 1
      this.loadData()
    },
    onPaginationChange({ page, perPage }) {
      this.page = page
      this.pageSize = perPage
      this.loadData()
    },
    async loadData() {
      try {
        this.$store.dispatch('app/loading', true)
        const params = { ...this.buildFilterParams(), page: this.page - 1, size: this.pageSize }
        const resp = await LeaveService.list(params)
        const pageData = resp.payload || {}
        this.list = pageData.content || []
        this.total = pageData.totalElements || 0
        this.pages = pageData.totalPages || 0
        this.selectedIds = []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar las licencias')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async downloadReport() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await LeaveService.download(this.buildFilterParams())
        const today = new Date().toISOString().split('T')[0]
        const url = window.URL.createObjectURL(resp.data)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `licencias-${today}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el reporte')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    openCancel(item) {
      this.confirmCancel = { show: true, id: item.id }
    },
    async doCancel() {
      try {
        this.$store.dispatch('app/loading', true)
        await LeaveService.cancel(this.confirmCancel.id)
        Alert.success('Licencia cancelada correctamente')
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cancelar la licencia')
      } finally {
        this.confirmCancel = { show: false, id: null }
        this.$store.dispatch('app/loading', false)
      }
    },
    triggerDocUpload(item) {
      this.docTargetId = item.id
      this.$refs.docInput.click()
    },
    async onDocSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      if (file.size > 10 * 1024 * 1024) {
        Alert.error('El archivo no debe superar 10MB')
        this.$refs.docInput.value = ''
        return
      }
      try {
        this.$store.dispatch('app/loading', true)
        await LeaveService.uploadDocument(this.docTargetId, file)
        Alert.success('Documento adjuntado correctamente')
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al subir el documento')
      } finally {
        this.$store.dispatch('app/loading', false)
        this.$refs.docInput.value = ''
        this.docTargetId = null
      }
    },
  },
  async created() {
    try {
      const [companiesResp, typesResp] = await Promise.all([
        CompanyService.listAll(),
        LeaveTypeService.list({ status: 1 }),
      ])
      this.filterOptions[0].options = (companiesResp.payload || []).map(c => ({ key: c.id, label: c.name }))
      this.filterOptions[1].options = (typesResp.payload || []).map(t => ({ key: t.id, label: t.name }))
    } catch (e) { console.error(e) }
    // Drill-down del dashboard "Licencias sin documentación".
    if (this.$route.query.pendingDoc === 'true') this.pendingDoc = true
    if (this.$route.query.companyId) this.$set(this.filters, 'companyId', Number(this.$route.query.companyId))
    await this.loadData()
  },
}
</script>

<style lang="stylus"></style>
